/**
 * Primary name cell for server-backed grids.
 *
 * The row's identity column: a link to the row's home carrying the
 * semibold primary text, optional adornments beside it (a status dot,
 * type icons), and an optional secondary caption line (a description)
 * underneath. Grids pass content; the typography, ellipsis handling,
 * and link styling live here once.
 */
import { Stack, Typography } from '@mui/material';
import React from 'react';

import GridLinkCell from './GridLinkCell';

/**
 * @param {object} props
 * @param {string} props.to - Route the cell links to.
 * @param {React.ReactNode} props.primary - The name text.
 * @param {React.ReactNode} [props.secondary] - Caption line under the name
 *   (rendered only when truthy).
 * @param {React.ReactNode} [props.leading] - Adornment before the name,
 *   inside the link (e.g. a status dot).
 * @param {React.ReactNode} [props.trailing] - Adornment after the name,
 *   inside the link (e.g. add-on icons).
 * @param {number} [props.spacing] - Stack spacing between name and adornments.
 * @param {object} [props.linkSx] - Extra sx merged onto the link wrapper.
 */
export default function GridNameCell({ to, primary, secondary, leading, trailing, spacing = 0.5, linkSx }) {
  return (
    <GridLinkCell to={to} sx={{ overflow: 'hidden', width: '100%', ...linkSx }}>
      <Stack direction="row" spacing={spacing} alignItems="center" sx={{ minWidth: 0 }}>
        {leading}
        <Typography noWrap sx={{ color: 'primary.main', fontWeight: 'var(--weight-semibold)' }}>
          {primary}
        </Typography>
        {trailing}
      </Stack>
      {secondary ? (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {secondary}
        </Typography>
      ) : null}
    </GridLinkCell>
  );
}
